'use client';

import { Input, Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { Typography } from 'components';
import { ENTER } from 'constants/keyboard';
import { UniversalChatMessageDestroyInput } from 'gql/graphql';
import { toaster } from 'helpers/toaster';
import { BallonIcon, EditIcon, ThreeDotsIcon, TrashIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { useUniversalChatThreadDelete } from 'providers/ChatProviders/useUniversalChatThreadDelete';
import { useUniversalChatThreadUpdate } from 'providers/ChatProviders/useUniversalChatThreadUpdate';
import { ChangeEvent, KeyboardEvent, MouseEvent, useCallback, useState } from 'react';
import { notifyException } from 'services/appsignal';
import { ESpansException } from 'types/ESpans';
import { cn } from 'utils/cn';
import { dateShort } from 'utils/time/dateShort';

interface IHistoryItem {
  id?: string;
  chatId?: string;
  title?: string;
  updatedAt?: string;
  refetch?: () => void;
}

export default function HistoryItem({
  refetch,
  chatId,
  id,
  title: titleIncoming = '',
  updatedAt
}: IHistoryItem) {
  const maxTitle = 30;
  const t = useTranslations();
  const [renameEnabled, setRenameEnabled] = useState(false);

  const initalTitle = titleIncoming?.slice(0, maxTitle);

  const [title, setTitle] = useState(initalTitle);

  const router = useRouter();

  const { mutateAsync: requestDelete } = useUniversalChatThreadDelete({
    onError: (error: unknown) => {
      notifyException(error, ESpansException.AiException);
      toaster.error(
        t('messages.error.chat_delete.paragraph'),
        t('messages.error.chat_delete.title')
      );
    }
  });

  const { mutateAsync: requestRename } = useUniversalChatThreadUpdate({
    onError: (error: unknown) => {
      notifyException(error, ESpansException.AiException);
      toaster.error(
        t('messages.error.chat_rename.paragraph'),
        t('messages.error.chat_rename.title')
      );
    }
  });

  const onOpenThreeDots = () => {};

  const onDeleteChat = useCallback(
    async (e: MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      await requestDelete({
        threadId: id as string
      } as UniversalChatMessageDestroyInput);
      refetch?.();
      if (id === chatId) router.push('/smart-tasks/chat-ai');
    },
    [id, chatId, router, refetch, requestDelete]
  );

  const onRenameEnabled = (e: MouseEvent<HTMLButtonElement>) => {
    e.stopPropagation();
    setRenameEnabled(true);
  };

  const onRenameChat = useCallback(async () => {
    requestRename({
      title,
      threadId: id as string
    });
    setRenameEnabled(false);
    refetch?.();
  }, [refetch, requestRename, title, id]);

  const onRenameKeyDown = (e: KeyboardEvent<HTMLInputElement>) =>
    e.key === ENTER && !e.shiftKey && onRenameChat();

  const onRenameChange = (e: ChangeEvent<HTMLInputElement>) => setTitle(e.target.value);

  return (
    <Link href={`/smart-tasks/chat-ai/${id}`}>
      <div
        className={cn(
          'group flex cursor-pointer justify-between rounded-3xl border p-2 pt-1 transition-all',
          ' hover:bg-tertiary-800 ',
          chatId === id ? '!border-primary-blue-100 bg-primary-blue-900' : '!border-tertiary-600'
        )}
      >
        <div className="flex w-full items-start gap-2">
          <BallonIcon
            className={cn(
              'mt-1',
              chatId === id ? '!text-primary-blue-100' : '!text-secondary-gray'
            )}
          />
          <div className="flex w-full flex-col">
            <Typography variant="regular4" className="truncate text-tertiary-100 ">
              {renameEnabled ? (
                <Input
                  value={title || ''}
                  onBlur={onRenameChat}
                  onKeyDown={onRenameKeyDown}
                  onChange={onRenameChange}
                  autoFocus
                  className="mb-0.5 w-full rounded-3xl border-1 border-primary-blue-100 pl-0.5 outline-none"
                />
              ) : (
                <span>{title + (title?.length > maxTitle ? '...' : '')}</span>
              )}
            </Typography>
            <Typography variant="light5" className="text-tertiary-200 ">
              {dateShort(updatedAt)}
            </Typography>
          </div>
        </div>
        <div className="relative flex h-5 w-6 flex-col items-end justify-end pt-3">
          <Menu>
            <MenuButton
              className="absolute !m-0 h-4 w-4 !outline-1 !outline-offset-0 outline-primary-blue-200"
              onClick={onOpenThreeDots}
            >
              <div data-tooltip-id="tolq-tooltip" data-tooltip-content={t('buttons.options')}>
                <ThreeDotsIcon className="transition-all hover:fill-tertiary-100" />
              </div>
            </MenuButton>
            <Transition
              enter="transition ease-out duration-75"
              enterFrom="opacity-0 scale-95"
              enterTo="opacity-100 scale-100"
              leave="transition ease-in duration-100"
              leaveFrom="opacity-100 scale-100"
              leaveTo="opacity-0 scale-95"
            >
              <MenuItems
                anchor="bottom end"
                className="w-36 origin-top-right rounded-md bg-white p-1 text-sm/6 text-red-500 shadow-md [--anchor-gap:var(--spacing-1)] focus:outline-none"
              >
                <MenuItem>
                  <button
                    className="flex w-full items-center gap-1 rounded-3xl p-1.5 data-[focus]:bg-primary-blue-900"
                    onClick={onRenameEnabled}
                  >
                    <div className="relative h-6 w-6">
                      <EditIcon className="absolute left-0.5 top-1 h-6 w-6 !text-tertiary-100" />
                    </div>
                    <Typography variant="medium5" className="!mt-0.5 text-tertiary-100 ">
                      {t('chat.history.rename')}
                    </Typography>
                  </button>
                </MenuItem>
                <MenuItem>
                  <button
                    className="flex w-full items-center gap-1 rounded-3xl p-1.5 data-[focus]:bg-primary-blue-900"
                    onClick={onDeleteChat}
                  >
                    <TrashIcon className="h-6 w-6 scale-[0.7] !text-secondary-red" />
                    <Typography variant="medium5" className="!mt-0.5 text-secondary-red ">
                      {t('chat.history.delete')}
                    </Typography>
                  </button>
                </MenuItem>
              </MenuItems>
            </Transition>
          </Menu>
        </div>
      </div>
    </Link>
  );
}
